/**
 * NeTEx XML File Import Component
 *
 * Handles the complete XML import pipeline:
 * 1. File upload to landing bucket
 * 2. Automatic validation (XSD schema + reference checks)
 * 3. Automatic apply (database import) on successful validation
 * 4. Status polling with visual feedback
 *
 * ERSAD MODIFICATIONS (December 2025):
 * - Added _applyTriggered flag to prevent multiple apply triggers
 * - Added polling error handling with retry count
 * - Enhanced PIPELINE_STATUS with help text for user guidance
 * - Added pollingError state for transient error display
 * - Fixed status transition handling for terminal states
 * - Enhanced polling error visibility with "Connection Issue" banner
 * - Made VALIDATED (blue/info) and APPLYING (yellow/warning) visually distinct
 * - Gated verbose console.log statements behind window.config.debug flag
 *
 * @module DropzoneNetexFile
 */

import PropTypes from "prop-types";
import { Component } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";

import MdCheckCircle from "@mui/icons-material/CheckCircle";
import MdDelete from "@mui/icons-material/Delete";
import MdError from "@mui/icons-material/Error";
import MdRefresh from "@mui/icons-material/Refresh";
import MdUpload from "@mui/icons-material/UploadFile";
import MdWarning from "@mui/icons-material/Warning";
import {
  Alert,
  Backdrop,
  Chip,
  CircularProgress,
  Collapse,
  LinearProgress,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import "../../styles/UploadFile.css";

import axios from "axios";
import { getTheme } from "../../config/themeConfig";

import DropzoneArea from "./DropZoneArea";

// Styled Backdrop Komponente
const MyBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: 1000,
}));

/**
 * Pipeline status definitions with UI properties.
 *
 * ERSAD: Added 'help' property for user-facing guidance text.
 * Each status maps to a specific UI representation (color, icon) and help message.
 *
 * IMPORTANT: VALIDATED is an intermediate state - the file passed schema checks
 * but database import hasn't completed yet. Only COMPLETED means full success.
 *
 * Status colors:
 * - VALIDATED = 'info' (blue) - validation complete, preparing import
 * - APPLYING = 'warning' (yellow/orange) - active DB import, DO NOT INTERRUPT
 */
const PIPELINE_STATUS = {
  UPLOADED: {
    label: "Uploaded",
    color: "info",
    icon: "upload",
    help: "File uploaded successfully. Validation will start automatically.",
  },
  VALIDATING: {
    label: "Validating...",
    color: "warning",
    icon: "loading",
    help: "Checking XML structure and NeTEx schema compliance.",
  },
  VALIDATED: {
    label: "Validation Passed - File is Error-Free",
    color: "success", // Grün statt blau
    icon: "check", // Checkmark statt loading
    help: "XML validation passed with no errors or warnings. Import will start automatically.",
  },
  VALIDATION_FAILED: {
    label: "Validation Failed",
    color: "error",
    icon: "error",
    help: "XML validation failed. This file contains errors or warnings that must be fixed before import. Please upload a corrected file.",
  },
  APPLYING: {
    label: "Step 2/2: IMPORTING TO DATABASE...",
    color: "warning",
    icon: "loading",
    help: "Database import in progress. Do NOT close this page or retry until complete.",
  },
  COMPLETED: {
    label: "Import Completed Successfully",
    color: "success",
    icon: "check",
    help: "All data imported successfully! You can now search for the imported stops.",
  },
  APPLY_FAILED: {
    label: "Import Failed",
    color: "error",
    icon: "error",
    // ERSAD: Clarified recovery path - backend requires new upload, not retry
    help: "Database import failed after validation passed. To retry: fix the issue in your XML file and upload again. The backend does NOT support retrying a failed job.",
  },
  ERROR: {
    label: "Connection Error",
    color: "error",
    icon: "error",
    help: 'Lost connection to server. Click "Retry" to check the current status of your upload.',
  },
};

function getBaseUploadUrl() {
  const url = window.config.tiamatUploadUrl?.replace(/\/$/, "");
  if (!url) {
    throw new Error("tiamatUploadUrl is not configured");
  }
  return url;
}

class DropzoneNetexFile extends Component {
  constructor(props) {
    super(props);
    /**
     * Component state for tracking upload and pipeline progress.
     *
     * ERSAD additions:
     * - pollingError: Shows transient errors without blocking the UI
     * - pollRetryCount: Tracks consecutive failures to trigger permanent error after 10
     * - _applyTriggered: Instance flag (not state) to prevent duplicate apply calls
     */
    this.state = {
      responses: [],
      open: true,
      files: [],
      fileRejections: [],
      showBackdrop: false,
      // Upload progress state
      uploadProgress: 0,
      uploadStatus: null, // 'uploading', 'processing', 'success', 'error'
      uploadId: null,
      // Pipeline status state
      pipelineStatus: null,
      pipelineMessage: null,
      errorCount: 0,
      warningCount: 0,
      errorDetails: null,
      siteFrameId: null,
      showDetails: false,
      // ERSAD: Polling status for resilient status checking
      pollingError: null, // Transient polling error message (non-blocking)
      pollRetryCount: 0, // Number of consecutive poll failures (triggers ERROR after 10)
    };
    this.handleSave = this.handleSave.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.parseError = this.parseError.bind(this);
    this.pollInterval = null;
    // AbortController for cancelling in-flight requests on unmount
    this.abortController = null;
  }

  componentWillUnmount() {
    // Clean up polling interval
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    // Cancel any in-flight requests
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  async handleSave() {
    // Precondition check: ensure a file is selected before uploading
    if (!this.state.files || this.state.files.length === 0) {
      this.setState({
        responses: [
          {
            status: 0,
            config: { data: { name: "unknown" } },
            data: "Please select a file before uploading.",
          },
        ],
        uploadStatus: "error",
      });
      return;
    }

    this.setShowBackdrop(true);
    this.setState({
      uploadProgress: 0,
      uploadStatus: "uploading",
      pipelineStatus: null,
      pipelineMessage: null,
      errorCount: 0,
      warningCount: 0,
      errorDetails: null,
      siteFrameId: null,
    });
    // ERSAD: Reset apply trigger flag for new upload
    // This flag prevents multiple apply triggers if VALIDATED status is received multiple times
    this._applyTriggered = false;
    await this.handleUpload();
  }

  async handleUpload() {
    this.upload(this.state.files[0])
      .then((success) => {
        // Debug logging gated behind config flag
        if (window.config?.debug) {
          console.log("Upload response:", success);
          console.log("Upload response data:", success.data);
          console.log("Upload ID from response:", success.data?.uploadId);
        }

        this.setResponses([success]);
        // Trigger validation after successful upload
        if (success.data?.uploadId) {
          if (window.config?.debug)
            console.log(
              "Upload successful, triggering validation for uploadId:",
              success.data.uploadId,
            );
          // Trigger validation explicitly instead of waiting for backend
          this.triggerValidate(success.data.uploadId);
        } else {
          // No uploadId - internal diagnostic, gated behind debug
          if (window.config?.debug) {
            console.warn("No uploadId in response - cannot poll for status");
            console.warn(
              "Full response:",
              JSON.stringify(success.data, null, 2),
            );
          }
        }
      })
      .catch((error) => {
        // Gate error logging to avoid noise in production
        if (window.config?.debug) {
          console.error("Upload error:", error.message || error);
          console.error("Upload error details:", error);
        }
        if (error.response) {
          this.setResponses([error.response]);
        } else {
          const errorResponse = {
            status: 0,
            config: {
              data: this.state.files[0] || { name: "unknown" },
            },
            data: error.message || "Network error or authentication failed",
          };
          this.setResponses([errorResponse]);
        }
        this.setState({ uploadStatus: "error" });
      });
  }

  // Start polling for pipeline status updates
  startStatusPolling(uploadId) {
    // Debug logging gated behind config flag
    if (window.config?.debug) {
      console.log("Starting status polling for uploadId:", uploadId);

      const baseURL = getBaseUploadUrl();
      console.log("Status URL will be:", baseURL + "/status/" + uploadId);
    }

    // Clear any existing interval
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }

    // Poll immediately, then every 2 seconds
    this.checkPipelineStatus(uploadId);
    this.pollInterval = setInterval(() => {
      this.checkPipelineStatus(uploadId);
    }, 2000);
  }

  /**
   * Check pipeline status and handle state transitions.
   *
   * ERSAD MODIFICATIONS:
   * - Added _applyTriggered flag check to prevent duplicate apply calls
   * - Added pollingError/pollRetryCount for resilient error handling
   * - Stops polling after 10 consecutive failures
   * - Auth errors (401/403) stop polling immediately
   *
   * @param {string} uploadId - The upload ID to check status for
   */
  async checkPipelineStatus(uploadId) {
    const baseURL = getBaseUploadUrl();
    const statusUrl = baseURL + "/status/" + encodeURIComponent(uploadId);

    try {
      const token = await this.props.auth.getAccessToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      const response = await axios.get(statusUrl, {
        headers: { Authorization: "Bearer " + token },
      });

      const status = response.data;

      this.setState({
        pipelineStatus: status.status,
        pipelineMessage: status.statusMessage,
        errorCount: status.errorCount || 0,
        warningCount: status.warningCount || 0,
        errorDetails: status.errorDetails,
        siteFrameId: status.siteFrameId,
        // ERSAD: Clear polling error on successful response
        pollingError: null,
        pollRetryCount: 0,
      });

      // Handle terminal states - stop polling
      if (
        ["COMPLETED", "VALIDATION_FAILED", "APPLY_FAILED"].includes(
          status.status,
        )
      ) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
        return;
      }

      // ERSAD: Auto-trigger apply when validated (only if not already triggered)
      // The _applyTriggered flag prevents multiple apply requests if polling
      // receives VALIDATED status multiple times before the apply starts.
      if (status.status === "VALIDATED" && !this._applyTriggered) {
        this._applyTriggered = true; // Prevent multiple apply triggers
        clearInterval(this.pollInterval);
        this.pollInterval = null;
        // Auto-apply after a short delay so user sees the VALIDATED status briefly
        setTimeout(() => this.triggerApply(uploadId), 500);
      }
    } catch (error) {
      // Gate error logging to avoid noise in production
      if (window.config?.debug) {
        console.error(
          "Error checking pipeline status:",
          error.message || error,
        );
        console.error("Status check failed - URL:", statusUrl);
        if (error.response) {
          console.error("Response status:", error.response.status);
          console.error("Response data:", error.response.data);
        }
      }

      // ERSAD: Track consecutive poll failures for resilient error handling
      const newRetryCount = (this.state.pollRetryCount || 0) + 1;

      // If we get 401/403, stop polling - it's not transient
      if (error.response?.status === 401 || error.response?.status === 403) {
        if (window.config?.debug)
          console.error("Authentication error - stopping poll");
        clearInterval(this.pollInterval);
        this.pollInterval = null;
        this.setState({
          pipelineStatus: "ERROR",
          pipelineMessage: "Authentication failed while checking status",
          pollingError: null,
          pollRetryCount: 0,
        });
        return;
      }

      // Show non-blocking notice for transient errors
      this.setState({
        pollingError: `Could not check status (attempt ${newRetryCount}), retrying...`,
        pollRetryCount: newRetryCount,
      });

      // Stop polling after 10 consecutive failures
      if (newRetryCount >= 10) {
        if (window.config?.debug)
          console.error("Too many polling failures - stopping");
        clearInterval(this.pollInterval);
        this.pollInterval = null;
        this.setState({
          pipelineStatus: "ERROR",
          pipelineMessage:
            "Lost connection to server. Click Retry to check status.",
          pollingError: null,
          pollRetryCount: 0,
        });
      }
    }
  }

  // Trigger the validation step
  async triggerValidate(uploadId) {
    try {
      this.setState({
        pipelineStatus: "VALIDATING",
        pipelineMessage: "Validating XML structure and schema...",
      });

      const token = await this.props.auth.getAccessToken();
      if (!token) {
        throw new Error("No authentication token available");
      }
      const validateUrl =
        getBaseUploadUrl() + "/validate/" + encodeURIComponent(uploadId);

      if (window.config?.debug) {
        console.log("Triggering validation:", validateUrl);
      }

      await axios.post(validateUrl, null, {
        headers: { Authorization: "Bearer " + token },
      });

      // Start polling to track validation progress
      this.startStatusPolling(uploadId);
    } catch (error) {
      if (window.config?.debug) {
        console.error("Error triggering validation:", error.message || error);
        if (error.response) {
          console.error("Validation error response:", error.response.data);
        }
      }

      let errorMessage = "Validation failed";
      let errorDetails = error.message;

      if (error.response?.data) {
        const respData = error.response.data;
        errorMessage =
          respData.message ||
          respData.error ||
          respData.statusMessage ||
          errorMessage;
        errorDetails =
          respData.errorDetails || respData.details || error.message;
      }

      this.setState({
        pipelineStatus: "VALIDATION_FAILED",
        pipelineMessage: errorMessage,
        errorDetails: errorDetails,
      });
    }
  }

  // Trigger the apply step
  async triggerApply(uploadId) {
    try {
      this.setState({
        pipelineStatus: "APPLYING",
        pipelineMessage: "Importing to database...",
      });

      const token = await this.props.auth.getAccessToken();
      if (!token) {
        throw new Error("No authentication token available");
      }
      const baseURL = getBaseUploadUrl();
      const applyUrl = baseURL + "/apply/" + encodeURIComponent(uploadId);

      await axios.post(applyUrl, null, {
        headers: { Authorization: "Bearer " + token },
      });

      // Start polling again to track apply progress
      this.startStatusPolling(uploadId);
    } catch (error) {
      // Gate error logging to avoid noise in production
      if (window.config?.debug) {
        console.error("Error triggering apply:", error.message || error);
        console.error("Apply error details:", error);
      }
      // Extract the best error message from the response
      let errorMessage = "Failed to start import";
      let errorDetails = error.message;

      if (error.response?.data) {
        const respData = error.response.data;
        // Handle different error response formats
        errorMessage =
          respData.message ||
          respData.error ||
          respData.statusMessage ||
          errorMessage;
        errorDetails =
          respData.errorDetails || respData.details || error.message;
      }

      this.setState({
        pipelineStatus: "APPLY_FAILED",
        pipelineMessage: errorMessage,
        errorDetails: errorDetails,
      });
    }
  }

  /**
   * Handle retry/recovery actions based on current pipeline status.
   *
   * ERSAD: Recovery path documentation:
   * - VALIDATION_FAILED: User must fix XML errors and re-upload (calls handleRemove to reset)
   * - APPLY_FAILED: User must fix data issues (missing topographic places, conflicts) and re-upload
   *   NOTE: Backend does NOT support retry for APPLY_FAILED - the job cannot be re-applied.
   *   The user must upload a new file after fixing the issue.
   * - ERROR (connection): Can retry polling to check if job actually completed
   */
  async handleRetry() {
    if (
      this.state.pipelineStatus === "VALIDATION_FAILED" ||
      this.state.pipelineStatus === "APPLY_FAILED"
    ) {
      // ERSAD: For both validation and apply failures, user must fix and re-upload
      // Backend does NOT support retry endpoint for APPLY_FAILED jobs
      // Clear the current file to allow new upload
      this.handleRemove();
    } else if (this.state.pipelineStatus === "ERROR" && this.state.uploadId) {
      // For connection errors, try to resume status polling
      this.setState({
        pipelineStatus: null,
        pipelineMessage: null,
        pollingError: null,
        pollRetryCount: 0,
      });
      this.startStatusPolling(this.state.uploadId);
    }
  }

  async upload(file) {
    let token;
    try {
      token = await this.props.auth.getAccessToken();
    } catch (err) {
      return Promise.reject(new Error("No authentication token available"));
    }

    if (!token) {
      return Promise.reject(new Error("No authentication token available"));
    }

    // Create new AbortController for this upload
    this.abortController = new AbortController();

    if (window.config?.debug)
      console.log("Using staged upload to:", getBaseUploadUrl(), file.name);
    // New staged upload: file goes to S3 first, then validated separately
    return axios
      .post(getBaseUploadUrl() + "/upload", file, {
        timeout: 20 * 60 * 1000, // 20 min timeout for large files
        signal: this.abortController.signal, // Allow cancellation on unmount
        headers: {
          "Content-Type": "application/xml;charset=UTF-8",
          Authorization: "Bearer " + token,
          "X-Filename": file.name, // Pass filename to backend
        },
        // Track upload progress for better UX
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          this.setState({ uploadProgress: percent });
          // Once upload hits 100%, we're waiting for server to process
          if (percent === 100) {
            this.setState({ uploadStatus: "processing" });
          }
        },
      })
      .then((response) => {
        // Debug logging gated behind config flag
        if (window.config?.debug) {
          console.log("Staged upload response received:", response);
          console.log("Response status:", response.status);
          console.log("Response headers:", response.headers);
          console.log("Response data:", response.data);
          console.log("Response data type:", typeof response.data);
        }

        // Check if response.data is a string (maybe JSON not parsed?)
        let data = response.data;
        if (typeof data === "string") {
          if (window.config?.debug)
            console.log("Response data is string, attempting to parse JSON");
          try {
            data = JSON.parse(data);
            if (window.config?.debug) console.log("Parsed data:", data);
          } catch (e) {
            if (window.config?.debug)
              console.error("Failed to parse response as JSON:", e);
          }
        }

        const uploadId = data?.uploadId || data?.upload_id;
        if (window.config?.debug) console.log("Extracted uploadId:", uploadId);

        // Staged upload returns JSON with uploadId
        this.setState({
          uploadId: uploadId,
          uploadStatus: "success",
        });
        return { ...response, data: data };
      });
  }

  handleChange(files) {
    this._applyTriggered = false; // Reset apply trigger flag
    this.setState({
      fileRejections: [],
      files: files,
      responses: [],
      // Reset upload state when new file selected
      uploadProgress: 0,
      uploadStatus: null,
      uploadId: null,
      // Reset pipeline state
      pipelineStatus: null,
      pipelineMessage: null,
      errorCount: 0,
      warningCount: 0,
      errorDetails: null,
      siteFrameId: null,
      showDetails: false,
      pollingError: null,
      pollRetryCount: 0,
    });
    // Clear any existing polling
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  handleRemove() {
    this._applyTriggered = false; // Reset apply trigger flag
    this.setState({
      files: [],
      fileRejections: [],
      responses: [],
      uploadProgress: 0,
      uploadStatus: null,
      uploadId: null,
      // Reset pipeline state
      pipelineStatus: null,
      pipelineMessage: null,
      errorCount: 0,
      warningCount: 0,
      errorDetails: null,
      siteFrameId: null,
      showDetails: false,
      pollingError: null,
      pollRetryCount: 0,
    });
    // Clear any existing polling
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  setResponses(responses) {
    this.setState({
      responses: responses,
      files: [],
      fileRejections: [],
    });
    this.setShowBackdrop(false);
  }

  handleRejectedFile(fileRejections) {
    this.setState({
      fileRejections: fileRejections,
      responses: [],
      files: [],
    });
    this.setShowBackdrop(false);
  }

  parseError(response) {
    if (!response || !response.data) {
      return "An unknown error occurred";
    }

    const fileName = response.config?.data?.name || "unknown";
    const rejectedMessage = "The file " + fileName + " has been rejected.";

    try {
      // Check if response.data is already an object (JSON response)
      if (typeof response.data === "object") {
        // Handle JSON error response from staged upload endpoint
        const errorData = response.data;
        if (errorData.message) {
          return rejectedMessage + "\n" + errorData.message;
        }
        if (errorData.error) {
          return rejectedMessage + "\n" + errorData.error;
        }
        if (errorData.status) {
          return rejectedMessage + "\nStatus: " + errorData.status;
        }
        // Fallback: stringify the object
        return rejectedMessage + "\n" + JSON.stringify(errorData, null, 2);
      }

      // Handle XML error response (legacy)
      let parser = new DOMParser();
      let xmlDoc = parser.parseFromString(response.data, "text/xml");
      let errorMessage = xmlDoc.getElementsByTagName("message")[0];
      if (errorMessage) {
        return rejectedMessage + "\n" + errorMessage.firstChild.data;
      } else {
        // Return raw response.data if no <message> found
        return rejectedMessage + "\n" + (response.data || "");
      }
    } catch (parseError) {
      if (window.config?.debug)
        console.error("Error parsing response:", parseError);
      return (
        "Error parsing server response: " + (response.data || "Unknown error")
      );
    }
  }

  setShowBackdrop(show) {
    this.setState({
      showBackdrop: show,
    });
  }

  render() {
    const { intl } = this.props;
    const { formatMessage } = intl;

    const DropzoneAreaWrapperStyle = {
      top: 60,
      background: "#fff",
      height: "auto",
      width: "50%",
      margin: 8,
      position: "absolute",
      zIndex: 999,
      padding: 8,
      border: "1px solid " + getTheme().borderColor,
    };

    return (
      <div className="Upload" style={DropzoneAreaWrapperStyle}>
        <span className="Title">
          {formatMessage({ id: "upload_file_dialog" })}
        </span>
        <span className="Title2">
          {formatMessage({ id: "upload_file_title" })}
        </span>
        <DropzoneArea
          setFiles={this.handleChange.bind(this)}
          setRejectedFileMessage={this.handleRejectedFile.bind(this)}
        />

        {/* File List */}
        <div>
          {this.state.files.map((file) => {
            return (
              <div key={file.name} style={{ width: "100%" }}>
                <Box display="flex" p={0.5} bgcolor="background.paper">
                  <Box alignSelf="center" width="100%">
                    <div className={"Filename"}>Selected file: {file.name}</div>
                  </Box>
                  <Box alignSelf="center" flexShrink={0}>
                    <Button
                      startIcon={
                        <MdDelete style={{ height: "1.3em", width: "1.3em" }} />
                      }
                      variant="text"
                      size="small"
                      style={{ margin: "8 5", zIndex: 999, fontSize: "0.7em" }}
                      onClick={this.handleRemove}
                    >
                      {formatMessage({ id: "delete_file" })}
                    </Button>
                    <Button
                      startIcon={
                        <MdUpload style={{ height: "1.3em", width: "1.3em" }} />
                      }
                      variant="contained"
                      size="small"
                      style={{ margin: "8 5", zIndex: 999, fontSize: "0.7em" }}
                      onClick={this.handleSave}
                    >
                      {formatMessage({ id: "upload_file" })}
                    </Button>
                  </Box>
                </Box>
              </div>
            );
          })}
        </div>

        {/* Upload Progress Bar - shows during upload */}
        {this.state.uploadStatus &&
          this.state.uploadStatus !== "error" &&
          !this.state.pipelineStatus && (
            <Box sx={{ width: "100%", mt: 2 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {this.state.uploadStatus === "uploading" &&
                    `Uploading... ${this.state.uploadProgress}%`}
                  {this.state.uploadStatus === "processing" &&
                    "Processing on server..."}
                  {this.state.uploadStatus === "success" && "Upload complete!"}
                </Typography>
              </Box>
              <LinearProgress
                variant={
                  this.state.uploadStatus === "processing"
                    ? "indeterminate"
                    : "determinate"
                }
                value={this.state.uploadProgress}
                color={
                  this.state.uploadStatus === "success" ? "success" : "primary"
                }
              />
            </Box>
          )}

        {/* Pipeline Status Display */}
        {this.state.pipelineStatus && (
          <Box sx={{ mt: 2 }}>
            {/* Polling Error Notice - shows connection issues prominently */}
            {this.state.pollingError && (
              <Alert
                severity="warning"
                sx={{ mb: 1, border: "2px solid", borderColor: "warning.main" }}
                variant="filled"
                icon={<MdWarning />}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Connection Issue
                    </Typography>
                    <Typography variant="body2">
                      {this.state.pollingError} (will stop after 10 failures)
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}

            {/* Status Alert - color-coded by state */}
            <Alert
              severity={
                // Visual distinction between states:
                // COMPLETED = 'success' (green) - final success
                // Error states = 'error' (red) - failures
                // VALIDATED = 'info' (blue) - validation done, preparing next step
                // VALIDATING/APPLYING = 'warning' (yellow) - active processing, don't interrupt!
                // UPLOADED = 'info' (blue)
                this.state.pipelineStatus === "COMPLETED"
                  ? "success"
                  : ["VALIDATION_FAILED", "APPLY_FAILED", "ERROR"].includes(
                        this.state.pipelineStatus,
                      )
                    ? "error"
                    : ["VALIDATING", "APPLYING"].includes(
                          this.state.pipelineStatus,
                        )
                      ? "warning"
                      : "info" // UPLOADED, VALIDATED show as info
              }
              icon={
                // ERSAD: VALIDATED now shows spinner since apply is starting automatically
                ["VALIDATING", "APPLYING", "VALIDATED"].includes(
                  this.state.pipelineStatus,
                ) ? (
                  <CircularProgress size={20} color="inherit" />
                ) : this.state.pipelineStatus === "COMPLETED" ? (
                  <MdCheckCircle />
                ) : ["VALIDATION_FAILED", "APPLY_FAILED", "ERROR"].includes(
                    this.state.pipelineStatus,
                  ) ? (
                  <MdError />
                ) : undefined
              }
              action={
                ["VALIDATION_FAILED", "APPLY_FAILED", "ERROR"].includes(
                  this.state.pipelineStatus,
                ) && (
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<MdRefresh />}
                    onClick={() => this.handleRetry()}
                  >
                    {this.state.pipelineStatus === "APPLY_FAILED"
                      ? "Clear & Upload New File"
                      : this.state.pipelineStatus === "VALIDATION_FAILED"
                        ? "Clear & Upload Fixed File"
                        : "Retry Status Check"}
                  </Button>
                )
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {PIPELINE_STATUS[this.state.pipelineStatus]?.label ||
                  this.state.pipelineStatus}
              </Typography>
              {this.state.pipelineMessage && (
                <Typography variant="body2">
                  {this.state.pipelineMessage}
                </Typography>
              )}
              {/* Help text for current status */}
              {PIPELINE_STATUS[this.state.pipelineStatus]?.help && (
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, fontStyle: "italic", opacity: 0.85 }}
                >
                  {PIPELINE_STATUS[this.state.pipelineStatus].help}
                </Typography>
              )}
              {/* VALIDATION_FAILED with blocking warnings - special notice */}
              {this.state.pipelineStatus === "VALIDATION_FAILED" &&
                this.state.warningCount > 0 &&
                this.state.errorCount === 0 && (
                  <Alert
                    severity="info"
                    sx={{
                      mt: 1,
                      borderLeft: "4px solid",
                      borderColor: "warning.main",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                      Import Blocked by Warnings
                    </Typography>
                    <Typography variant="body2">
                      This file contains {this.state.warningCount} validation
                      warning(s). While the XML structure is valid, these issues
                      must be resolved before import. All warnings are treated
                      as blocking errors for data quality assurance.
                    </Typography>
                  </Alert>
                )}{" "}
              {/* ERSAD: Additional APPLY_FAILED guidance for topographic place issues */}
              {this.state.pipelineStatus === "APPLY_FAILED" && (
                <Box
                  sx={{ mt: 1, p: 1, bgcolor: "error.light", borderRadius: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "error.contrastText", fontWeight: "bold" }}
                  >
                    {formatMessage({ id: "apply_xml_file_failed" })}
                  </Typography>
                </Box>
              )}
            </Alert>

            {/* Status Details */}
            <Box
              sx={{
                mt: 1,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {this.state.uploadId && (
                <Chip
                  label={`ID: ${this.state.uploadId}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {this.state.siteFrameId && (
                <Chip
                  label={`SiteFrame: ${this.state.siteFrameId}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {this.state.errorCount > 0 && (
                <Chip
                  icon={<MdError />}
                  label={`${this.state.errorCount} error(s)`}
                  size="small"
                  color="error"
                />
              )}
              {this.state.warningCount > 0 && (
                <Chip
                  icon={<MdWarning />}
                  label={`${this.state.warningCount} warning(s)`}
                  size="small"
                  color="warning"
                />
              )}
            </Box>

            {/* Error/Warning Details (expandable) */}
            {this.state.errorDetails && (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() =>
                    this.setState({ showDetails: !this.state.showDetails })
                  }
                >
                  {this.state.showDetails ? "Hide Details" : "Show Details"}
                </Button>
                <Collapse in={this.state.showDetails}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      maxHeight: 200,
                      overflow: "auto",
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {this.state.errorDetails}
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* Pipeline Progress Indicator */}
            {["VALIDATING", "APPLYING"].includes(this.state.pipelineStatus) && (
              <LinearProgress sx={{ mt: 1 }} />
            )}
          </Box>
        )}

        {/* File Rejection Errors */}
        <div>
          {this.state.fileRejections.map(({ file, errors }) => {
            return (
              <Alert
                key={file.path}
                severity="error"
                style={{ marginTop: "10px" }}
              >
                <ul style={{ margin: 0 }}>
                  {errors.map((e) => (
                    <li key={e.code}>{e.message}</li>
                  ))}
                </ul>
              </Alert>
            );
          })}
        </div>

        {/* Upload Response Messages - only show if no pipeline status yet */}
        <div>
          {!this.state.pipelineStatus &&
            this.state.responses.map((response, index) => {
              // Check for staged upload response (JSON with uploadId)
              if (response.status === 200 && response.data?.uploadId) {
                // Don't show this anymore - pipeline status will handle it
                return null;
              }
              // Legacy success response (XML)
              if (response.status === 200) {
                return (
                  <Alert
                    key={response.config?.data?.lastModified || index}
                    severity="success"
                    style={{ marginTop: "10px" }}
                  >
                    The file {response.config?.data?.name || "unknown"} has been
                    processed successfully.
                  </Alert>
                );
              } else {
                return (
                  <Alert
                    key={response.config?.data?.lastModified || index}
                    severity="error"
                    style={{ marginTop: "10px" }}
                  >
                    {response.status === 0
                      ? `Error: ${response.data}`
                      : this.parseError(response)}
                  </Alert>
                );
              }
            })}
        </div>

        {/* Loading Backdrop - only show when not using progress bar */}
        <MyBackdrop open={this.state.showBackdrop && !this.state.uploadStatus}>
          <CircularProgress color="inherit" />
        </MyBackdrop>
      </div>
    );
  }
}

/**
 * PropTypes for DropzoneNetexFile component.
 *
 * @property {object} auth - Authentication provider with getAccessToken() method
 * @property {object} intl - react-intl internationalization object (injected)
 */
DropzoneNetexFile.propTypes = {
  auth: PropTypes.shape({
    getAccessToken: PropTypes.func.isRequired,
  }).isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

export default injectIntl(connect()(DropzoneNetexFile));
